import { Category } from '@@partner:types:app'
import React, { useCallback, useEffect, useState } from 'react'

import { useCategoriesFetcher } from '@@partner:graphql/query/catalog/categoriesWithParent.gql'
import { useMutateBulkUpdateCategoriesOrder } from '@@partner:graphql/mutation/catalog/bulkUpdateCategoriesOrder'

import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable'
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core'

import { Button, Checkbox } from 'rsuite'
import CategoryItem from './CategoryItem'
import CategoriesList from './CategoriesList'

import './CategoryOrderGrid.scss'

import { notifyOk } from '@@partner:helpers/notification'

export default function CategoryOrderGrid () {
  const [ data, setData ] = useState<Category[]>([])
  const [ selectedCategory, setSelectedCategory ] = useState<Category | null>(null)
  const [ selectedSubcategory, setSelectedSubcategory ] = useState<Category | null>(null)

  const [ showOnlyActive, setShowOnlyActive ] = useState(false)

  const filteredData = !showOnlyActive ? data : data.filter((cat) => cat.status === 1)

  const { fetchRootCategories } = useCategoriesFetcher()
  const [ bulkUpdateCategories ] = useMutateBulkUpdateCategoriesOrder()

  useEffect(() => {
    const preload = async () => {
      const initial = await fetchRootCategories({ skipIds: [], style: 'orig_' })
      const sortedCategories = [ ...initial.categories.edges ].sort((a, b) => {
        return (b.customer_order || 0) - (a.customer_order || 0)
      })
      setData(sortedCategories)
    }

    preload()
  }, [ fetchRootCategories ])

  const handleEditCategories = useCallback((newCategories: Category[]) => {
    const categoriesWithOrder = newCategories.map((category, index) => ({
      ...category,
      customer_order: (newCategories.length - index) * 10
    }))
    setData(categoriesWithOrder)
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id && data.length > 0) {
      const oldIndex = data.findIndex((i) => i.id === active.id)
      const newIndex = data.findIndex((i) => i.id === over?.id)
      const newBlocks = arrayMove(data, oldIndex, newIndex)
      handleEditCategories(newBlocks)
    }
  }, [ data, handleEditCategories ])

  const handleCategoryChange = useCallback((category: Category) => {
    const newCategories = data.map((cat) => {
      if (cat.id === category.id) {
        return category
      }
      return cat
    })
    handleEditCategories(newCategories)
  }, [ data, handleEditCategories ])

  const handleCategorySelect = useCallback((category: Category) => {
    setSelectedCategory(category)
    setSelectedSubcategory(null) // Сбрасываем выбор подкатегории
  }, [])

  const handleSubcategorySelect = useCallback((subcategory: Category) => {
    setSelectedSubcategory(subcategory)
  }, [])

  const handleUpdateCategories = useCallback(async () => {
    const toSave = data.map((category) => ({
      id: category.id,
      customer_order: category.customer_order,
      preview_size: category.preview_size,
      icon_id: category.icon_id,
      alternative_icon_id: category.alternative_icon_id,
      mini_icon_id: category.mini_icon_id
    }))

    const { data: saveData } = await bulkUpdateCategories({
      variables: {
        categoriesInfo: toSave
      }
    })

    if (saveData) {
      notifyOk('Порядок категорий сохранен')
    }
  }, [ data, bulkUpdateCategories ])
  
  return (
    <div className="CategoryOrderGrid">
      <div className="CategoryOrderGrid__left-panel">
        <div className="CategoryOrderGrid__header">
          <div>
            <h1 className="u-heading">Порядок и обложки</h1>
            <Checkbox
              checked={showOnlyActive}
              onChange={() => {
                setShowOnlyActive(!showOnlyActive)
              }}
            >
              Показать только активные категории
            </Checkbox>
          </div>
          <Button
            appearance="primary"
            onClick={handleUpdateCategories}
            className="CategoryOrderGrid__save"
          >
            Сохранить
          </Button>
        </div>
        <div className="CategoryOrderGrid__content CategoryOrderGrid__grid">
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={filteredData.map((category) => category.id)}
              strategy={rectSortingStrategy}
            >
              { filteredData.map((category) => {
                return (
                  <CategoryItem 
                    key={category.id} 
                    category={category}
                    onChange={handleCategoryChange}
                    isSelected={selectedCategory?.id === category.id}
                    onSelect={handleCategorySelect}
                  />
                )
              }) }
            </SortableContext>
          </DndContext>
        </div>
      </div>
      
      <div className="CategoryOrderGrid__right-panel">
        <div className="CategoryOrderGrid__subcategories">
          <CategoriesList 
            parentCategory={selectedCategory} 
            level={2}
            title="Подкатегории"
            onCategorySelect={handleSubcategorySelect}
            selectedCategory={selectedSubcategory}
          />
        </div>
        
        <div className="CategoryOrderGrid__subsubcategories">
          <CategoriesList 
            parentCategory={selectedSubcategory} 
            level={3}
            title="Под-подкатегории"
          />
        </div>
      </div>
    </div>
  )
}